import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFindError } from 'hooks/useFindError';
import { useForm } from 'react-hook-form';
import {
  authActions,
  httpRequestsOnErrorsActions,
  httpRequestsOnSuccessActions,
} from 'store';
import { CREATE_USER } from 'store/auth/types';
import { inputName } from './data';
import { Images } from 'assets/images';
import { ROUTES } from 'utils/routeNames';
import { INPUT_PASSWORD_TYPE, INPUT_TEXT_TYPE } from 'utils/constant';
import { yupResolver } from '@hookform/resolvers/yup';
import { signUpScheme } from 'validationSchemes';
import { SocialLogin } from 'components/socialLogin';
import { selectAccessToken, selectAuth } from 'store/auth/selector';
import { LoadingButton } from 'components';
import { useFindLoad } from 'hooks/useFindLoad';
import { useFindSuccess } from 'hooks/useFindSuccess';
import { useNavigate } from 'react-router-dom';
import { setStorage } from 'utils/storageHelpers';
import { LazyContainer } from 'components/ImageLazy/styled';
import { ImageLazy } from 'components/ImageLazy';
import {
  InputBlock,
  PasswordVisibleIcon,
  StyledInput,
} from 'components/inputs/inputStyles';
import {
  AuthenticationLink,
  SignUpText,
  SuggestionText,
  TextBold,
} from '../login/styled';
import {
  CloseIconLink,
  RegisterBlock,
  RegisterFormWrapper,
  TitlePage,
  CloseIcon,
  RegisterFormBlock,
  SuggestionBlock,
  RegistrationErrorMessage,
  StyledButtonContainer,
} from './styled';
import { EditEmailModal } from 'components/editEmailModal/editEmailModal';

const Register = () => {
  const user = useSelector(selectAuth);
  const accessToken = useSelector(selectAccessToken);
  const [inputType, setInputType] = useState('password');
  const [showEmailModal, setShowEmailModal] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    watch,
  } = useForm({
    resolver: yupResolver(signUpScheme),
  });

  const registrationError = useFindError(CREATE_USER);
  const registrationSuccess = useFindSuccess(CREATE_USER);
  const isLoading = useFindLoad(CREATE_USER);

  const togglePasswordVisibility = () => {
    setInputType((prev) =>
      prev === INPUT_PASSWORD_TYPE ? INPUT_TEXT_TYPE : INPUT_PASSWORD_TYPE,
    );
  };

  const onSubmit = (data) => {
    const { email, name, password } = data;

    const body = {
      name: name,
      email: email,
      password: password,
    };
    dispatch(authActions.userReg(body));
  };

  useEffect(() => {
    const { email } = getValues();
    if (registrationSuccess.length) {
      dispatch(httpRequestsOnSuccessActions.removeSuccess(CREATE_USER));
      navigate(ROUTES.VERIFY_EMAIL);
      setStorage('email', email);
    }
  }, [navigate, registrationSuccess.length]);

  useEffect(() => {
    dispatch(httpRequestsOnErrorsActions.removeError(CREATE_USER));
  }, [watch('name'), watch('email'), watch('password')]);

  useEffect(() => {
    if (accessToken && user) {
      navigate(ROUTES.HOME);
    }
  }, [accessToken, user]);

  return (
    <RegisterFormWrapper>
      <LazyContainer>
        <ImageLazy
          src={Images.loginMainBackground}
          width="100%"
          height="100%"
          hash="LqBE%hJDxWWBX=xaaxa}x^awWYof"
        />
      </LazyContainer>
      <RegisterBlock>
        <RegisterFormBlock
          onSubmit={handleSubmit(onSubmit)}
          autoComplete="new-password"
        >
          <TitlePage>Sign Up</TitlePage>
          <CloseIconLink to={ROUTES.LOGIN}>
            <CloseIcon src={Images.closeBlueIcon} />
          </CloseIconLink>

          <InputBlock>
            <StyledInput
              type="text"
              placeholder="Full Name"
              autoComplete="new-password"
              {...register(inputName.name)}
              error={
                errors?.name?.message || registrationError?.[0]?.error?.data?.errors
              }
            />
          </InputBlock>
          {errors?.name?.message ? (
            <RegistrationErrorMessage>
              {errors?.name?.message}
            </RegistrationErrorMessage>
          ) : null}

          <InputBlock>
            <StyledInput
              type="text"
              placeholder="Email Address"
              {...register(inputName.email)}
              error={
                errors?.email?.message || registrationError?.[0]?.error?.data?.errors
              }
            />
          </InputBlock>

          {errors?.email?.message ? (
            <RegistrationErrorMessage>
              {errors?.email?.message}
            </RegistrationErrorMessage>
          ) : registrationError?.[0]?.error?.data?.errors ? (
            <RegistrationErrorMessage>
              The email has already been taken
            </RegistrationErrorMessage>
          ) : null}

          <InputBlock>
            <StyledInput
              placeholder="Password"
              type={inputType}
              {...register(inputName.password)}
              autoComplete="new-password"
              error={
                errors?.password?.message ||
                registrationError?.[0]?.error?.data?.errors
              }
            />
            <PasswordVisibleIcon
              src={
                inputType === INPUT_PASSWORD_TYPE
                  ? Images.passwordClosedEye
                  : Images.passwordOpenedEye
              }
              onClick={togglePasswordVisibility}
            />
          </InputBlock>

          <RegistrationErrorMessage>
            {errors?.password?.message}
          </RegistrationErrorMessage>

          <StyledButtonContainer>
            <LoadingButton
              width="100%"
              height="54px"
              fs="20px"
              loading={isLoading}
              name="Sign Up"
              type="submit"
            />
          </StyledButtonContainer>

          <SuggestionBlock>
            <TextBold>Or Sign In with</TextBold>
            <SocialLogin setShowEmailModal={setShowEmailModal} />
            <SuggestionText>
              <SignUpText>Already have an account?</SignUpText>
              <AuthenticationLink to={ROUTES.LOGIN}>Sign In</AuthenticationLink>
            </SuggestionText>
          </SuggestionBlock>
        </RegisterFormBlock>
      </RegisterBlock>
      {showEmailModal ? (
        <EditEmailModal setShowEmailModal={setShowEmailModal} />
      ) : null}
    </RegisterFormWrapper>
  );
};

export default Register;
